<template>
  <div class="row">
    <div class="col-md-12">
      <h3>
        <a role="button" class="me-2" @click="goBack">
          <i class="material-icons">arrow_back</i>
        </a>
        {{ toolGroupDisplayName }}
      </h3>
    </div>
  </div>

  <div class="row">
    <div class="col-md-12 mb-3">
      <card :title="$t('tools.geoTraces')">
        <div class="d-flex flex-wrap align-items-center gap-3 mb-3 range-picker-row">
          <div class="d-flex align-items-center gap-2">
            <label class="form-label mb-0 me-1">{{ $t('tools.dateRange') }}</label>
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              :unlink-panels="!isMobileViewport"
              range-separator="—"
              :start-placeholder="$t('common.startDate') || 'Start'"
              :end-placeholder="$t('common.endDate') || 'End'"
              format="DD/MM/YYYY"
              value-format="YYYY-MM-DD"
              :shortcuts="rangeShortcuts"
              :clearable="false"
              :editable="false"
              placement="bottom-start"
              popper-class="car-geotraces-date-popper"
              @change="onFiltersChange"
            />
          </div>

          <span class="text-muted text-sm ms-auto">
            {{ traces.length }} {{ $t('tools.geoTraces').toLowerCase() }}
          </span>
        </div>

        <div v-if="toolGroup && toolGroup.deviceId" class="d-flex flex-wrap align-items-center gap-2 mb-3">
          <device-status-badge :last-contact="toolGroup.deviceLastContact" />
          <span v-if="deviceSummaryText" class="text-sm text-muted">{{ deviceSummaryText }}</span>
        </div>

        <div v-if="loading" class="text-muted">{{ $t('common.loading') || 'Loading...' }}</div>
        <div v-else-if="traces.length === 0" class="text-muted">{{ $t('tools.noGeoTraces') }}</div>
        <template v-else>
          <div class="row geotraces-layout">
            <!-- Map: top on mobile, right on desktop -->
            <div class="col-12 col-lg-8 order-1 order-lg-2 mb-3 mb-lg-0">
              <div id="carGeoTracesMap" class="map map-big"></div>
            </div>
            <!-- List: below on mobile, left on desktop -->
            <div class="col-12 col-lg-4 order-2 order-lg-1">
              <ul class="list-group trace-list">
                <li
                  v-for="t in traces"
                  :key="t.id"
                  class="list-group-item d-flex align-items-center trace-row"
                  :class="{ active: isActive(t) }"
                  @mouseenter="highlightTrace(t)"
                  @click="highlightTrace(t)"
                >
                  <span class="trace-icon trace-icon-device me-3">
                    <i class="material-icons">sensors</i>
                  </span>
                  <div class="flex-grow-1 min-width-0">
                    <div class="fw-semibold text-truncate">{{ titleFor(t) }}</div>
                    <div class="text-sm text-muted text-truncate">{{ t.address || $t('tools.gpsDeviceTrace') }}</div>
                    <div class="text-sm">
                      {{ formatTimestamp(t.timestamp) }}
                      &middot;
                      {{ t.latitude.toFixed(5) }}, {{ t.longitude.toFixed(5) }}
                    </div>
                  </div>
                  <a
                    role="button"
                    class="ms-2"
                    :title="$t('tools.showOnMap') || 'Show on map'"
                    @click.stop="focusOnTrace(t)"
                  >
                    <i class="material-icons">place</i>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </template>
      </card>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import Card from '@/components/Card.vue';
import DeviceStatusBadge, { isInContact, appLocale } from '@/components/DeviceStatusBadge.vue';
import { ElDatePicker } from 'element-plus';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export default {
  name: 'CarGeoTraces',
  components: { Card, DeviceStatusBadge, ElDatePicker },
  data() {
    const toIsoDate = d => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    const today = new Date();
    const monthAgo = new Date();
    monthAgo.setDate(monthAgo.getDate() - 30);
    return {
      toolGroupId: this.$route.params.toolGroupId,
      toolGroup: null,
      traces: [],
      loading: true,
      map: null,
      markers: {},
      activeTraceKey: null,
      isMobileViewport: window.innerWidth < 768,
      dateRange: [toIsoDate(monthAgo), toIsoDate(today)],
      rangeShortcuts: [
        {
          text: this.$t('tools.rangeLast7Days') || 'Last 7 days',
          value: () => {
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 7);
            return [start, end];
          },
        },
        {
          text: this.$t('tools.rangeLast30Days') || 'Last 30 days',
          value: () => {
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 30);
            return [start, end];
          },
        },
        {
          text: this.$t('tools.rangeLast90Days') || 'Last 90 days',
          value: () => {
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 90);
            return [start, end];
          },
        },
        {
          text: this.$t('tools.rangeLastYear') || 'Last year',
          value: () => {
            const end = new Date();
            const start = new Date();
            start.setFullYear(start.getFullYear() - 1);
            return [start, end];
          },
        },
      ],
    };
  },
  computed: {
    toolGroupDisplayName() {
      return this.toolGroup ? this.toolGroup.name : '';
    },
    deviceSummaryText() {
      const deviceTraces = this.traces.filter(t => t.timestamp);
      if (deviceTraces.length === 0) return '';
      const newest = deviceTraces.reduce((a, b) => (new Date(a.timestamp) > new Date(b.timestamp) ? a : b));
      const date = this.formatTimestamp(newest.timestamp);
      // Devices only report a new position when they move, so with a live
      // device an old last fix means "stationary", not "no signal"
      return isInContact(this.toolGroup?.deviceLastContact)
        ? this.$t('tools.stationarySince', { date })
        : this.$t('tools.lastKnownLocation', { date });
    },
  },
  async mounted() {
    await Promise.all([this.fetchToolGroup(), this.fetchTraces()]);
    this.loading = false;
    this.$nextTick(() => this.initMap());
    window.addEventListener('resize', this.handleMapResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleMapResize);
    if (this._mapResizeTimer) clearTimeout(this._mapResizeTimer);
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    goBack() {
      // Shared by cars and vinnuvélar — go back to whichever detail page we came from
      const target = this.$route.name === 'Heavy Machine GeoTraces' ? 'Heavy Machine' : 'Car';
      this.$router.push({ name: target, params: { toolGroupId: this.toolGroupId } });
    },
    handleMapResize() {
      this.isMobileViewport = window.innerWidth < 768;
      if (!this.map) return;
      if (this._mapResizeTimer) clearTimeout(this._mapResizeTimer);
      this._mapResizeTimer = setTimeout(() => {
        if (this.map) this.map.invalidateSize();
      }, 100);
    },
    async fetchToolGroup() {
      try {
        const response = await api.get(`/ToolGroup/${this.toolGroupId}`);
        this.toolGroup = response.data;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
      }
    },
    async fetchTraces() {
      try {
        const params = {};
        if (this.dateRange && this.dateRange[0]) {
          params.from = `${this.dateRange[0]}T00:00:00`;
        }
        if (this.dateRange && this.dateRange[1]) {
          params.to = `${this.dateRange[1]}T23:59:59`;
        }
        const response = await api.get(`/GeoTrace/ForToolGroup/${this.toolGroupId}`, { params });
        this.traces = response.data;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },
    async onFiltersChange() {
      this.loading = true;
      this.activeTraceKey = null;
      // Tear down existing map so it rebuilds for the new dataset
      if (this.map) {
        this.map.remove();
        this.map = null;
        this.markers = {};
      }
      await this.fetchTraces();
      this.loading = false;
      this.$nextTick(() => this.initMap());
    },
    titleFor(trace) {
      const dt = trace.deviceTypeName || this.$t('tools.deviceType');
      return `${dt} — ${trace.deviceId}`;
    },
    formatTimestamp(ts) {
      if (!ts) return '';
      try {
        return new Date(ts).toLocaleString(appLocale());
      } catch {
        return ts;
      }
    },
    initMap() {
      const el = document.getElementById('carGeoTracesMap');
      if (!el) return;
      this.map = L.map('carGeoTracesMap');
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(this.map);

      const points = this.traces.filter(t => t.latitude != null && t.longitude != null);
      if (points.length === 0) {
        this.map.setView([65.6835, -18.1009], 5);
        return;
      }

      points.forEach(t => {
        const marker = L.marker([t.latitude, t.longitude], { icon: this.markerIconFor() })
          .addTo(this.map)
          .bindPopup(this.popupHtml(t));
        this.markers[t.id] = marker;
      });

      if (points.length === 1) {
        this.map.setView([points[0].latitude, points[0].longitude], 15);
      } else {
        const bounds = L.latLngBounds(points.map(p => [p.latitude, p.longitude]));
        this.map.fitBounds(bounds.pad(0.2));
      }
    },
    markerIconFor() {
      return L.divIcon({
        className: 'car-geotrace-marker',
        html: `<div class="marker-bubble trace-marker-device"><i class="material-icons">sensors</i></div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });
    },
    popupHtml(trace) {
      const title = this.escape(this.titleFor(trace));
      const subtitle = this.escape(trace.address || this.$t('tools.gpsDeviceTrace'));
      const ts = this.escape(this.formatTimestamp(trace.timestamp));
      return `<div><strong>${title}</strong><br>${subtitle}<br><small>${ts}</small></div>`;
    },
    escape(s) {
      if (s == null) return '';
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
    isActive(trace) {
      return this.activeTraceKey === trace.id;
    },
    highlightTrace(trace) {
      const m = this.markers[trace.id];
      if (!m || !this.map) return;
      this.activeTraceKey = trace.id;
      m.openPopup();
    },
    focusOnTrace(trace) {
      const m = this.markers[trace.id];
      if (!m || !this.map) return;
      this.activeTraceKey = trace.id;
      this.map.setView(m.getLatLng(), 16);
      m.openPopup();
    },
  },
};
</script>

<style scoped>
.map-big {
  height: 420px;
  width: 100%;
}

@media (min-width: 992px) {
  .map-big {
    height: 600px;
  }
  .trace-list {
    max-height: 600px;
    overflow-y: auto;
  }
}

.min-width-0 {
  min-width: 0;
}

.trace-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: #fff;
  flex-shrink: 0;
}

.trace-icon i {
  font-size: 20px;
}

.trace-icon-device {
  background-color: #2dce89;
}

.trace-row {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.trace-row:hover,
.trace-row.active {
  background-color: rgba(94, 114, 228, 0.08);
}

.range-picker-row .form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #344767;
}

.range-picker-row :deep(.el-date-editor) {
  --el-date-editor-daterange-width: 280px;
  width: auto;
  min-width: 280px;
}

@media (max-width: 575.98px) {
  .range-picker-row {
    flex-direction: column;
    align-items: stretch;
  }
  .range-picker-row > .d-flex {
    flex-direction: column;
    align-items: stretch !important;
    gap: 4px !important;
  }
  .range-picker-row :deep(.el-date-editor) {
    width: 100% !important;
    min-width: 0;
  }
  .range-picker-row .ms-auto {
    margin-left: 0 !important;
    text-align: right;
  }
}
</style>

<style>
.car-geotrace-marker .marker-bubble {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}

.car-geotrace-marker .marker-bubble i {
  font-size: 20px;
}

.car-geotrace-marker .trace-marker-device {
  background-color: #2dce89;
}

.car-geotraces-date-popper.el-popper {
  z-index: 9999 !important;
}

@media (max-width: 767.98px) {
  .car-geotraces-date-popper.el-popper {
    max-width: calc(100vw - 16px) !important;
    width: auto !important;
  }
  .car-geotraces-date-popper .el-picker-panel__sidebar {
    width: 100px !important;
  }
  .car-geotraces-date-popper .el-picker-panel__body {
    margin-left: 100px !important;
  }
  .car-geotraces-date-popper .el-picker-panel__shortcut {
    padding-left: 8px !important;
    font-size: 12px !important;
  }
}
</style>
